'use client';

import React from 'react';

import OutlinedFlag from '@mui/icons-material/OutlinedFlag';
import IconButton, { type IconButtonProps } from '@mui/material/IconButton';

import { useUserInfo } from '@/context/UserInfoContext';
import { FEEDBACK_TRIGGERS, useFeedback, type Target } from '@/feedback';

/** Props for the shared feedback flag button that opens targeted feedback. */
type FeedbackFlagButtonProps = Omit<IconButtonProps, 'children' | 'onClick'> & {
  target: Target;
};

/**
 * Shared flag IconButton that opens targeted feedback and is safe inside clickable cards.
 *
 * Self-gates on auth: renders nothing for signed-out users, since feedback flagging
 * requires an authenticated session. This mirrors the OmniBoxMenu self-gate convention,
 * so call sites can render it without wrapping it in their own `userInfo` guard.
 */
export const FeedbackFlagButton: React.FC<FeedbackFlagButtonProps> = ({
  target,
  size = 'small',
  title = 'Give feedback',
  'aria-label': ariaLabel = 'Give feedback',
  ...props
}) => {
  const { openFeedback } = useFeedback();
  const { userInfo } = useUserInfo();

  if (!userInfo) {
    return null;
  }

  return (
    <IconButton
      {...props}
      type="button"
      size={size}
      title={title}
      aria-label={ariaLabel}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        openFeedback({ trigger: FEEDBACK_TRIGGERS.FLAG, target });
      }}
    >
      <OutlinedFlag fontSize="inherit" />
    </IconButton>
  );
};
